import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useGetUserById, useUpdateUserStatus, useDeleteUser } from "../../custom-hooks/use-users";
import moment from "moment";
import { Breadcrumbs, Button, Spinner, Tooltip } from "@material-tailwind/react";
import { convertToPresentableText, Notify, NotifyError } from "../../utils/functions";
import {
    CopyIcon,
    HomeIcon,
    IdCardIcon,
    MailIcon,
    PhoneIcon,
    UserIcon,
    CalendarIcon,
    ClockIcon,
    ShieldCheckIcon,
    BadgeCheckIcon,
    ArrowLeftIcon,
    Edit2Icon,
    TrashIcon,
    ShieldOffIcon,
    ShieldCheckIcon as ShieldRestoreIcon,
    LogInIcon
} from "lucide-react";
import UserFormModal from "./userFormModal";
import { URLS } from "../../router-x/routes";
import { useAuth } from "../../services/contexts/authContext";
import { STATIC_USER_ROLES } from "../../utils/enums";
import { useConfirm } from "../../custom-hooks/confirm-dialog";

const UserDetailsPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { userDetails: currentUser, startImpersonation } = useAuth();
    const [isUserFormModalOpen, setIsUserFormModalOpen] = useState(false);
    const [impersonating, setImpersonating] = useState(false);
    const { confirm, ConfirmationDialog } = useConfirm();
    const queryClient = useQueryClient();

    const {
        data: userDetails,
        isLoading: isUserDetailsLoading,
        error: userDetailsError,
        refetch
    } = useGetUserById(userId);

    const updateStatusMutation = useUpdateUserStatus();
    const deleteUserMutation = useDeleteUser();

    const user = userDetails?.data?.user || {};
    const isAdmin = currentUser?.role === STATIC_USER_ROLES.ADMIN;
    const isOwnAccount = currentUser?._id === user?._id;

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Notify("Copied to clipboard", "success");
    };

    const handleRevokeAccess = async () => {
        const confirmed = await confirm({
            title: user.isActive ? 'Revoke Access' : 'Restore Access',
            message: `Are you sure you want to ${user.isActive ? 'revoke access for' : 'restore access for'} ${user.name}?`,
            confirmText: user.isActive ? 'Revoke Access' : 'Restore Access',
            confirmColor: user.isActive ? 'red' : 'green'
        });
        if (!confirmed) return;
        try {
            await updateStatusMutation.mutateAsync({ userId, status: !user.isActive });
            await refetch();
        } catch (error) {
            NotifyError("Failed to update user access", error);
        }
    };

    const handleDeleteUser = async () => {
        const confirmed = await confirm({
            title: 'Permanently Delete User',
            message: <div className="text-sm">
                You are about to permanently delete <span className="font-bold">{user.name}</span> ({user.email}).
                <div className="mt-2 text-red-600 font-semibold">This will:</div>
                <ul className="list-disc ml-5 mt-1">
                    <li>Remove the user from the system</li>
                </ul>
                <div className="mt-2 text-red-600 font-bold">This action CANNOT be undone.</div>
            </div>,
            confirmText: 'Delete Permanently',
            confirmColor: 'red',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;
        try {
            await deleteUserMutation.mutateAsync(userId);
            queryClient.removeQueries('users');
            navigate(URLS.ADMIN.USER);
        } catch (error) {
            NotifyError("Failed to delete user", error);
        }
    };

    const handleImpersonate = async () => {
        try {
            setImpersonating(true);
            await startImpersonation(userId, location.pathname);
            
            if (user.role === STATIC_USER_ROLES.USER) {
                navigate(URLS.CUSTOMER.HOME);
            } else if (user.role === STATIC_USER_ROLES.TEAM_MEMBER) {
                navigate(URLS.TEAM_MEMBER.HOME);
            } else if (user.role === STATIC_USER_ROLES.RECEPTIONIST) {
                navigate(URLS.RECEPTIONIST.HOME);
            } else if (user.role === STATIC_USER_ROLES.SUPPORT_AGENT) {
                navigate(URLS.SUPPORT_AGENT.HOME);
            } else {
                navigate(URLS.ADMIN.HOME);
            }
        } catch (err) {
            console.error('Failed to start impersonation:', err);
            setImpersonating(false);
        }
    };

    const isActionLoading = updateStatusMutation.isLoading || deleteUserMutation.isLoading || impersonating;

    if (isUserDetailsLoading) {
        return (
            <div className="flex justify-center items-center min-h-[80vh]">
                <div className="text-center">
                    <Spinner className="h-12 w-12 mx-auto mb-4" />
                    <p className="text-gray-600">Loading user details...</p>
                </div>
            </div>
        );
    }

    if (userDetailsError) {
        return (
            <div className="flex flex-col justify-center items-center min-h-[80vh] p-6">
                <div className="bg-red-50 p-8 rounded-xl text-center max-w-md w-full shadow-sm">
                    <div className="text-red-500 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading User</h2>
                    <p className="text-gray-600 mb-4">{userDetailsError.message || "Failed to load user details"}</p>
                    <div className="mb-6">
                        <span className="bg-gray-100 px-3 py-1 rounded-full text-gray-600 text-xs">User ID: {userId}</span>
                    </div>
                    <Button
                        onClick={() => navigate(URLS.ADMIN.USER)}
                        className="flex items-center gap-2 mx-auto"
                        variant="outlined"
                        color="blue-gray"
                    >
                        <ArrowLeftIcon size={16} />
                        Back to Users
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="m-4 p-4 md:p-6 max-w-7xl mx-auto border rounded-3xl">
            {/* Breadcrumbs */}
            <Breadcrumbs className="mb-6">
                <a href={URLS.ADMIN.ROOT} className="opacity-60 flex items-center gap-1 hover:text-blue-500 transition-colors">
                    <HomeIcon size={14} />
                    <span>Home</span>
                </a>
                <a href={URLS.ADMIN.USER} className="opacity-60 flex items-center gap-1 hover:text-blue-500 transition-colors">
                    <UserIcon size={14} />
                    <span>Users</span>
                </a>
                <span className="flex items-center gap-1 font-medium">
                    <UserIcon size={14} />
                    <span>{user?.name || 'User Details'}</span>
                </span>
            </Breadcrumbs>

            {/* Back Button */}
            <div className="mb-6">
                <Button
                    variant="text"
                    color="blue-gray"
                    className="flex items-center gap-2 px-4"
                    onClick={() => navigate(URLS.ADMIN.USER)}
                >
                    <ArrowLeftIcon size={16} />
                    List of Users
                </Button>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User Profile Card */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-32 relative">
                            {user.photoURL ? (
                                <img
                                    src={user.photoURL}
                                    alt={user.name}
                                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-24 h-24 rounded-full border-4 border-white object-cover"
                                />
                            ) : (
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-24 h-24 rounded-full border-4 border-white bg-blue-100 flex items-center justify-center text-blue-500 text-4xl font-bold">
                                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                                </div>
                            )}
                        </div>

                        <div className="pt-16 pb-6 px-6 text-center">
                            <h1 className="text-2xl font-bold text-gray-800">{user.name || "User Name"}</h1>
                            <p className="text-sm text-gray-500 mb-3">{user.role ? convertToPresentableText(user.role) : "User"}</p>

                            <div className="flex justify-center mb-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${user.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                    }`}>
                                    {user.isActive ? "Active" : "Inactive"}
                                </span>
                            </div>

                            <div className="flex flex-col space-y-3 text-left">
                                {user.email && (
                                    <a href={`mailto:${user.email}`} className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors hover:bg-gray-50">
                                        <MailIcon size={18} className="text-blue-500" />
                                        <span className="text-gray-700 truncate">{user.email}</span>
                                    </a>
                                )}

                                {user.phone && (
                                    <a href={`tel:${user.phone}`} className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors hover:bg-gray-50">
                                        <PhoneIcon size={18} className="text-green-500" />
                                        <span className="text-gray-700">{user.phone}</span>
                                    </a>
                                )}

                                {user.cnic && (
                                    <div className="flex items-center gap-3 px-4 py-2 rounded-lg">
                                        <IdCardIcon size={18} className="text-amber-500" />
                                        <span className="text-gray-700">{user.cnic}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* User Details */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-3xl border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                            <BadgeCheckIcon size={20} className="text-blue-500" />
                            Account Information
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">User ID</p>
                                <Tooltip content="Click to copy" placement="bottom">
                                    <div
                                        onClick={() => copyToClipboard(user._id)}
                                        className="flex items-center gap-2 text-gray-800 font-medium cursor-pointer hover:text-blue-600 transition-colors"
                                    >
                                        <span className="truncate">{user._id || "N/A"}</span>
                                        <CopyIcon size={14} />
                                    </div>
                                </Tooltip>
                            </div>

                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">Auth UID</p>
                                <Tooltip content="Click to copy" placement="bottom">
                                    <div
                                        onClick={() => copyToClipboard(user.uid)}
                                        className="flex items-center gap-2 text-gray-800 font-medium cursor-pointer hover:text-blue-600 transition-colors"
                                    >
                                        <span className="truncate">{user.uid || "N/A"}</span>
                                        <CopyIcon size={14} />
                                    </div>
                                </Tooltip>
                            </div>

                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">Role</p>
                                <div className="flex items-center gap-2">
                                    <ShieldCheckIcon size={16} className="text-blue-500" />
                                    <span className="text-gray-800 font-medium">{convertToPresentableText(user.role) || "User"}</span>
                                </div>
                            </div>

                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">Status</p>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${user.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                    }`}>
                                    {user.isActive ? "Active" : "Inactive"}
                                </span>
                            </div>

                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">Created On</p>
                                <div className="flex items-center gap-2">
                                    <CalendarIcon size={16} className="text-gray-400" />
                                    <span className="text-gray-800">
                                        {user.createdDate ? moment(user.createdDate).format("MMM DD, YYYY") : "N/A"}
                                    </span>
                                </div>
                            </div>

                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">Last Updated</p>
                                <div className="flex items-center gap-2">
                                    <ClockIcon size={16} className="text-gray-400" />
                                    <span className="text-gray-800">
                                        {user.lastUpdated ? moment(user.lastUpdated).format("MMM DD, YYYY") : "N/A"}
                                    </span>
                                </div>
                            </div>

                            <div className="border-b border-gray-100 pb-4">
                                <p className="text-sm text-gray-500 mb-1">Last Login</p>
                                <div className="flex items-center gap-2">
                                    <ClockIcon size={16} className="text-gray-400" />
                                    <span className="text-gray-800">
                                        {user.lastLogin ? moment(user.lastLogin).format("MMM DD, YYYY [at] h:mm A") : "Never"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 flex flex-wrap items-center gap-4">
                        <Button className="flex items-center gap-2 shadow-sm rounded-full" onClick={() => setIsUserFormModalOpen(true)}>
                            <Edit2Icon size={16} />
                            Edit User
                        </Button>

                        {/* Impersonate Button (hidden on own account) */}
                        {!isOwnAccount && (
                            <Button
                                color="indigo"
                                variant="outlined"
                                className="flex items-center gap-2 rounded-full"
                                onClick={handleImpersonate}
                                disabled={isActionLoading}
                            >
                                {impersonating ? <Spinner className="h-4 w-4" /> : <LogInIcon size={16} />}
                                {impersonating ? 'Switching...' : 'Login as this User'}
                            </Button>
                        )}

                        {/* Admin-only actions (hidden on own account) */}
                        {isAdmin && !isOwnAccount && (
                            <>
                                <Button
                                    color={user.isActive ? "amber" : "green"}
                                    variant="outlined"
                                    className="flex items-center gap-2 rounded-full"
                                    onClick={handleRevokeAccess}
                                    disabled={isActionLoading}
                                >
                                    {isActionLoading && updateStatusMutation.isLoading
                                        ? <Spinner className="h-4 w-4" />
                                        : user.isActive
                                            ? <ShieldOffIcon size={16} />
                                            : <ShieldRestoreIcon size={16} />
                                    }
                                    {user.isActive ? "Revoke Access" : "Restore Access"}
                                </Button>

                                <Button
                                    color="red"
                                    variant="text"
                                    className="flex items-center gap-2 rounded-full ml-auto"
                                    onClick={handleDeleteUser}
                                    disabled={isActionLoading}
                                >
                                    {isActionLoading && deleteUserMutation.isLoading
                                        ? <Spinner className="h-4 w-4" />
                                        : <TrashIcon size={16} />
                                    }
                                    Delete User
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {
                isUserFormModalOpen && (
                    <UserFormModal
                        isOpen={isUserFormModalOpen}
                        onClose={() => setIsUserFormModalOpen(false)}
                        isUpdating={true}
                        updatingUserId={userId}
                        defaultData={user}
                    />
                )
            }
            <ConfirmationDialog />
        </div>
    );
};

export default UserDetailsPage;

